import type { TransactionItem } from 'store/organisation/models';

export type TransactionItemOption = {
  label: string;
  value: string;
};

export type ItemDisplayRowFields = {
  itemId?: string | null;
};

export function buildTransactionItemOptions(
  items: TransactionItem[]
): TransactionItemOption[] {
  return items
    .map((item) => {
      const name = (item.name ?? '').trim();
      const id = (item.id ?? '').trim();
      if (!id) return null;
      return { label: name || id, value: id };
    })
    .filter((o): o is TransactionItemOption => o != null)
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
    );
}

export function buildItemNameByIdMap(
  items: TransactionItem[]
): Map<string, string> {
  const map = new Map<string, string>();
  for (const item of items) {
    const id = (item.id ?? '').trim();
    const name = (item.name ?? '').trim();
    if (!id || !name) continue;
    map.set(id, name);
    map.set(id.toLowerCase(), name);
  }
  return map;
}

export function buildItemNameByIdMapFromOptions(
  options: TransactionItemOption[]
): Map<string, string> {
  const map = new Map<string, string>();
  for (const option of options) {
    const id = String(option.value).trim();
    const name = (option.label ?? '').trim();
    if (!id || !name) continue;
    map.set(id, name);
    map.set(id.toLowerCase(), name);
  }
  return map;
}

function findItemOptionByKey(
  key: string,
  options: TransactionItemOption[]
): TransactionItemOption | undefined {
  const trimmed = key.trim();
  if (!trimmed) return undefined;
  const lower = trimmed.toLowerCase();
  return (
    options.find((o) => String(o.value) === trimmed) ??
    options.find((o) => String(o.value).toLowerCase() === lower) ??
    options.find((o) => o.label === trimmed)
  );
}

export function findItemOptionInList(
  needle: string | undefined,
  options: TransactionItemOption[]
): TransactionItemOption | undefined {
  if (!needle?.trim()) return undefined;
  return findItemOptionByKey(needle.trim(), options);
}

export function lookupItemDisplayInList(
  key: string,
  map: Map<string, string>,
  options: TransactionItemOption[]
): string | undefined {
  const trimmed = key.trim();
  if (!trimmed) return undefined;
  const lower = trimmed.toLowerCase();
  const opt = findItemOptionByKey(trimmed, options);
  const fromOption = (opt?.label ?? '').trim();
  if (fromOption) return fromOption;
  return map.get(trimmed) ?? map.get(lower);
}

export function resolveItemDisplayLabel(
  row: ItemDisplayRowFields | undefined,
  cellVal: unknown,
  itemNameByIdMap: Map<string, string>,
  transactionItemOptions: TransactionItemOption[]
): string {
  const itemId =
    row?.itemId != null && String(row.itemId).trim() !== ''
      ? String(row.itemId).trim()
      : '';
  const fromCell =
    cellVal != null && String(cellVal).trim() !== ''
      ? String(cellVal).trim()
      : '';

  const lookupKeys = [fromCell, itemId].filter(
    (k, i, arr) => k && arr.indexOf(k) === i
  );

  for (const key of lookupKeys) {
    const resolved = lookupItemDisplayInList(
      key,
      itemNameByIdMap,
      transactionItemOptions
    );
    if (resolved) return resolved;
  }

  if (fromCell) return fromCell;
  if (itemId) return itemId;
  return '-';
}

export function applyItemSelectionToTransactionFields(
  nextVal: string,
  itemNameByIdMap: Map<string, string>,
  transactionItemOptions: TransactionItemOption[]
): Pick<ItemDisplayRowFields, 'itemId'> {
  const trimmed = nextVal.trim();
  if (!trimmed) {
    return { itemId: null };
  }

  const matched = findItemOptionInList(trimmed, transactionItemOptions);
  const inListById =
    itemNameByIdMap.has(trimmed) ||
    itemNameByIdMap.has(trimmed.toLowerCase()) ||
    (matched != null &&
      (itemNameByIdMap.has(matched.value) ||
        itemNameByIdMap.has(matched.value.toLowerCase())));

  if (inListById) {
    return { itemId: matched?.value ?? trimmed };
  }

  return { itemId: trimmed };
}

export function buildItemSelectOptions(
  options: TransactionItemOption[],
  fallback?: TransactionItemOption | null
): TransactionItemOption[] {
  if (
    fallback &&
    !options.some(
      (o) =>
        String(o.value) === String(fallback.value) ||
        String(o.value).toLowerCase() ===
          String(fallback.value).toLowerCase()
    )
  ) {
    return [fallback, ...options];
  }
  return options;
}

export function resolveItemSelectEditState(params: {
  row: ItemDisplayRowFields | undefined;
  cellVal: unknown;
  itemNameByIdMap: Map<string, string>;
  transactionItemOptions: TransactionItemOption[];
}): {
  value: string | undefined;
  label: string | undefined;
  options: TransactionItemOption[];
} {
  const { row, cellVal, itemNameByIdMap, transactionItemOptions } = params;

  const displayLabelRaw = resolveItemDisplayLabel(
    row,
    cellVal,
    itemNameByIdMap,
    transactionItemOptions
  );
  const displayLabel =
    displayLabelRaw === '-' ? undefined : displayLabelRaw;

  const rawVal =
    (cellVal != null ? String(cellVal).trim() : '') ||
    (row?.itemId != null ? String(row.itemId).trim() : '') ||
    undefined;

  const matched =
    (rawVal
      ? findItemOptionInList(rawVal, transactionItemOptions)
      : undefined) ??
    (displayLabel
      ? findItemOptionInList(displayLabel, transactionItemOptions)
      : undefined);

  const value = matched?.value ?? rawVal;
  const label = matched?.label ?? displayLabel ?? value;

  const fallback =
    value && label
      ? matched
        ? null
        : { label, value }
      : null;

  const options = buildItemSelectOptions(transactionItemOptions, fallback);

  return { value, label, options };
}
